import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Badge from '../common/Badge';
import { useCart } from '../../hooks/useCart';
import { useWishlist } from '../../hooks/useWishlist';
import { theme } from '../../config/theme';
import { formatCurrency } from '../../utils/helpers';
import { getStockStatus, getPrimaryImage } from '../../utils/productHelpers';
import { TrashIcon, ShoppingCartIcon } from '../icons/Icons';

const WishlistItem = ({ item }) => {
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const { removeFromWishlist, loading: wishlistLoading } = useWishlist();
  const product = item.product;
  const stockStatus = getStockStatus(product);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    await addToCart(product.id, 1);
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    await removeFromWishlist(product.id);
  };

  const itemStyles = {
    display: 'grid',
    gridTemplateColumns: '120px 1fr auto',
    gap: theme.spacing.lg,
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    border: `1px solid ${theme.colors.gray[200]}`,
    cursor: 'pointer',
    transition: theme.transitions.base,
  };

  const imageStyles = {
    width: '120px',
    height: '120px',
    objectFit: 'cover',
    borderRadius: theme.borderRadius.md,
  };

  return (
    <div
      style={itemStyles}
      onClick={() => navigate(`/products/${product.slug}`)}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = theme.shadows.md;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <img 
        src={getPrimaryImage(product)} 
        alt={product.name}
        style={imageStyles}
      />

      <div>
        <h3 style={{ 
          fontSize: theme.typography.fontSize.lg, 
          fontWeight: theme.typography.fontWeight.semibold,
          marginBottom: theme.spacing.xs,
        }}>
          {product.name}
        </h3>
        
        {product.category && (
          <div style={{ 
            fontSize: theme.typography.fontSize.sm, 
            color: theme.colors.text.secondary,
            marginBottom: theme.spacing.sm,
          }}>
            {product.category.name}
          </div>
        )}

        <div style={{ marginBottom: theme.spacing.md }}>
          <Badge variant={stockStatus.color === 'success' ? 'success' : stockStatus.color === 'warning' ? 'warning' : 'error'}>
            {stockStatus.label}
          </Badge>
        </div>

        <div style={{ 
          fontSize: theme.typography.fontSize.xl, 
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.primary.main,
        }}>
          {formatCurrency(product.price)}
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: theme.spacing.sm,
        minWidth: '150px',
      }}>
        <Button
          variant="primary"
          size="sm"
          fullWidth
          onClick={handleAddToCart}
          loading={cartLoading}
          disabled={stockStatus.status === 'out_of_stock' || cartLoading}
        >
          <ShoppingCartIcon size={16} />
          Add to Cart
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          fullWidth
          onClick={handleRemove}
          disabled={wishlistLoading}
          style={{ color: theme.colors.error }}
        >
          <TrashIcon size={16} />
          Remove
        </Button>
      </div>
    </div>
  );
};

export default WishlistItem;